<template>
  <div id="admin-users-list" class="container-fluid item-list pb-64 admin-users-wide">
    <v-list-header
      :title="this.$t('admin.user.users')"
      icon="group"
      @open-filters-offcanvas="openOffCanvas('the-admin-user-filters-offcanvas')"
      @emit-close-off-canvas="closeOffCanvas"
      @reload-users="loadUsers"
    >
      <template #input-search>
        <v-input-text-search
          id="user-search"
          v-model="mix"
          :placeholder="this.$t('general.shared.search')"
          autocomplete="off"
          class="w-100 me-24"
          input-class="h-auto py-4"
          input-group-text-class="py-4 px-16"
          type="text"
          @update:modelValue="mix = $event; search()"
        />
      </template>
    </v-list-header>

    <div class="items-content">
      <div class="filters-container mb-12">
        <v-filters
          v-if="!!filterOptions"
          v-model="filterOptions"
          @remove-filter="removeFilter"
        />
      </div>

      <div class="list-table-shell mt-12">
        <div class="table-responsive d-none d-md-flex table-for-admin-users">
        <v-table>
          <v-table-header>
            <tr class="align-middle">
              <th>
                <v-button
                  :class="{ 'sort-active': order.key === 'name' }"
                  class="btn-xs btn-icon btn btn-order"
                  @click="toggleOrder('name')"
                >
                  {{ $t('general.shared.name') }}
                  <v-icon
                    :class="{ 'sort-icon-transition': order.key === 'name' && order.order === 'DESC' }"
                    icon="chevron-down" />
                </v-button>
              </th>

              <th class="text-nowrap">
                {{ $t('general.shared.email') }}
              </th>

              <th class="text-nowrap">
                {{ $t('admin.user.roles') || 'Roles' }}
              </th>

              <th class="text-nowrap">
                {{ $t('admin.user.agencies') }}
              </th>

              <th class="fit"/>
            </tr>
          </v-table-header>

          <v-table-body class="fw-medium">
            <tr v-if="!loaded">
              <td class="position-relative border-0 py-96" colspan="2222">
                <v-loader/>
              </td>
            </tr>

            <template v-else-if="!!users && users.length > 0">
              <tr v-for="user in users" :key="user.id">
                <td class="">
                  <div class="d-flex align-items-center">
                    <div class="image-container rounded-sm bg-primary me-12">
                      {{ !!user.name ? user.name.charAt(0) : '-' }}
                    </div>

                    <a
                      class="py-4 fw-bold text-nowrap"
                      href="#"
                      @click.prevent="showUserDetails(user)"
                    >
                      {{ user.name || '-' }}
                    </a>
                  </div>
                </td>

                <td class="text-nowrap" style="font-size: 12px;">{{ user.email }}</td>

                <td>
                  <div class="d-flex gap-4 flex-wrap" style="min-width: 150px; font-size: 12px;">
                    <span v-for="role in user.roles" :key="role.id" class="badge bg-light text-dark fw-normal border" style="font-size: 12px;">
                      {{ $t(`general.roles.${role.name}`) }}
                    </span>
                  </div>
                </td>

                <td>
                  <div class="text-wrap" style="min-width: 200px; max-width: 400px; font-size: 12px;">
                    <span v-if="user.agencies && user.agencies.length">
                      {{ user.agencies.map(a => a.name).join(', ') }}
                    </span>
                    <span v-else class="text-muted italic">
                      {{ $t('admin.user.noAgencies') }}
                    </span>
                  </div>
                </td>

                <td class="fit text-end">
                  <div class="d-flex align-items-center justify-content-end gap-4">
                    <v-button
                      v-if="user.id !== authStore.user?.id"
                      :title="$t('admin.user.loginAs')"
                      class="btn-view bg-warning border-warning text-dark"
                      size="xs"
                      @click="impersonate(user)"
                    >
                      <v-icon icon="user-settings" size="xs" class="text-dark"/>
                    </v-button>

                    <v-button
                      v-if="user.id !== authStore.user?.id"
                      :title="user.canAccess ? $t('admin.user.denyAccess') : $t('admin.user.provideAccess')"
                      :class="user.canAccess ? 'bg-danger border-danger text-white' : 'bg-success border-success text-white'"
                      class="btn-view"
                      size="xs"
                      @click="toggleAccess(user)"
                    >
                      <v-icon :icon="user.canAccess ? 'user-delete' : 'user-check'" size="xs" class="text-white"/>
                    </v-button>

                    <v-button
                      :title="$t('admin.user.userDetails')"
                      class="btn-view rounded-pill bg-primary border-primary text-white"
                      size="xs"
                      @click="showUserDetails(user)"
                    >
                      <v-icon icon="arrow-right" size="xs" class="text-white"/>
                    </v-button>
                  </div>
                </td>
              </tr>
            </template>

            <template v-else>
              <tr>
                <td class="rounded-xs py-16" colspan="2222">
                  {{ $t('admin.user.noUsers') }}
                </td>
              </tr>
            </template>
          </v-table-body>
        </v-table>
        </div>
      </div>

      <div class="item-cards d-block d-md-none">
        <div v-if="!loaded" class="position-relative py-96">
          <v-loader/>
        </div>

        <template v-else-if="!!users && users.length > 0">
          <div v-for="user in users" :key="user.id" class="item-card border rounded-xs p-12 mb-12 text-sm">
            <div class="d-flex align-items-center mb-12">
              <div class="image-container rounded-sm bg-primary me-12">
                {{ !!user.name ? user.name.charAt(0) : '' }}
              </div>

              <div>
                <div class="fw-bold">{{ user.name || '-' }}</div>
                <div class="fw-light text-email" style="font-size: 12px;">{{ user.email }}</div>
              </div>
            </div>

            <div class="mb-12">
              <div class="fw-medium text-uppercase text-muted mb-4" style="font-size: 10px;">{{ $t('admin.user.roles') || 'Roles' }}</div>
              <div class="d-flex gap-4 flex-wrap" style="font-size: 12px;">
                <span v-for="role in user.roles" :key="role.id" class="badge bg-light text-dark fw-normal border" style="font-size: 12px;">
                  {{ $t(`general.roles.${role.name}`) }}
                </span>
              </div>
            </div>

            <div v-if="user.agencies && user.agencies.length" class="mb-12">
              <div class="fw-medium text-uppercase text-muted mb-4" style="font-size: 10px;">{{ $t('admin.user.agencies') }}</div>
              <div style="font-size: 12px;">{{ user.agencies.map(a => a.name).join(', ') }}</div>
            </div>

            <div class="d-flex justify-content-end gap-8 pt-8 border-top">
              <v-button
                v-if="user.id !== authStore.user?.id"
                class="btn-view bg-warning border-warning text-dark"
                size="xs"
                variant="icon"
                @click="impersonate(user)"
              >
                <v-icon icon="user-settings" size="xs"/>
              </v-button>

              <v-button
                v-if="user.id !== authStore.user?.id"
                :class="user.canAccess ? 'bg-danger border-danger' : 'bg-success border-success'"
                class="btn-view text-white"
                size="xs"
                variant="icon"
                @click="toggleAccess(user)"
              >
                <v-icon :icon="user.canAccess ? 'user-delete' : 'user-check'" size="xs"/>
              </v-button>

              <v-button
                class="btn-view rounded-pill bg-primary border-primary text-white"
                size="xs"
                variant="icon"
                @click="showUserDetails(user)"
              >
                <v-icon icon="arrow-right" size="xs"/>
              </v-button>
            </div>
          </div>
        </template>

        <div v-else>
          {{ $t('admin.user.noUsers') }}
        </div>
      </div>

      <v-pagination
        v-if="!!users && users.length > 0"
        :meta="pagination.meta"
        :pagination-links="pagination.links"
        class="mt-4"
        @page-changed="changePage($event); handlePageChangedEvent($event)"
        @per-page-changed="perPage = $event; doSearch()"
      />
    </div>

    <the-admin-user-filters-offcanvas
      v-if="offCanvasToShow === 'the-admin-user-filters-offcanvas'"
      v-model:status="status"
      @closed="closeOffCanvas"
      @selected="doSearch"
    />

    <the-admin-user-details-offcanvas
      v-if="offCanvasToShow === 'the-admin-user-details-offcanvas'"
      :is-archived="status === 'archived'"
      :user-data="currentUser"
      @closed="closeOffCanvas(); cleanCurrentUser()"
      @reload-users="loadUsers"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { VInputTextSearch } from '@uniqoders/form';
import qs from 'qs';
import querystring from 'query-string';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VTable from '@/components/vendor/basic/table/VTable.vue';
import VTableHeader from '@/components/vendor/basic/table/VTableHeader.vue';
import VTableBody from '@/components/vendor/basic/table/VTableBody.vue';
import VLoader from '@/components/vendor/loader/VLoader.vue';
import api from '@/api';
import User from '@/api/objects/User';
import withTimeout from '@/helpers/timeOut';
import useOffCanvasUtils from '@/helpers/OffCanvasUtils';
import TheAdminUserDetailsOffcanvas from '@/components/admin/user/TheAdminUserDetailsOffcanvas.vue';
import TheAdminUserFiltersOffcanvas from '@/components/admin/user/TheAdminUserFiltersOffcanvas.vue';
import VListHeader from '@/components/shared/VListHeader.vue';
import VPagination from '@/components/vendor/basic/table/VPagination.vue';
import usePagination from '@/helpers/pagination';
import VFilters from '@/components/vendor/filters/VFilters.vue';
import { findConceptByKey } from '@/helpers/ConceptHelper';
import useCapabilities from '@/helpers/capabilities';
import { useAuthStore } from '@/stores/auth';

export default defineComponent({
  name: 'TheAdminUsers',
  components: {
    VFilters,
    VPagination,
    VListHeader,
    TheAdminUserFiltersOffcanvas,
    TheAdminUserDetailsOffcanvas,
    VLoader,
    VTableBody,
    VTableHeader,
    VTable,
    VButton,
    VInputTextSearch,
    VIcon,
  },
  setup() {
    const authStore = useAuthStore();

    return {
      ...useCapabilities(),
      ...useOffCanvasUtils(),
      ...usePagination(),
      authStore,
    };
  },
  data() {
    return {
      users: [] as User[],
      currentUser: null as null | User,
      status: 'active' as string,
      mix: '' as string,
      filters: [
        {
          variable: 'status',
          key: 'status',
          labelAux: 'general.shared.filterStatus',
          conceptName: 'app.states',
          multiple: false,
          isConcept: true,
        },
        {
          variable: 'mix',
          key: 'mix',
          labelAux: 'general.shared.filterSearch',
          multiple: false,
          isConcept: false,
        },
      ] as Record<string, string | boolean>[],
      order: {
        key: '' as string,
        order: '' as string,
      } as Record<string, any>,
      perPage: 15 as number,
      page: 1 as number,
      timers: {
        name: null as any,
      },
      loaded: false as boolean,
      filterOptions: [] as Record<string, any>[],
    };
  },
  async created() {
    await this.setQueryParametersAsFilters();
    await this.doSearch();
  },
  methods: {
    search() {
      clearTimeout(this.timers.name);

      this.timers.name = withTimeout(async () => {
        await this.doSearch();
      }, this.timers.name, 1000);
    },
    /**
     * Detects any query parameter in the url and loads it in the filters (if available)
     *
     * @returns {Promise<void>}
     */
    async setQueryParametersAsFilters() {
      const { query }: any = this.$route;

      const {
        filters,
        order,
        perPage,
        status,
        page,
      }: any = qs.parse(query);

      if (!!filters) {
        this.filters.forEach((filter: Record<string, any>) => {
          if (!!filters[filter.key]) {
            this[filter.variable] = filters[filter.key];
          }
        });
      }
      if (!!order) {
        this.order = order;
      }

      const pageNumber = parseInt(page, 10);

      if (!Number.isNaN(pageNumber)) {
        this.page = pageNumber;
      }

      const perPageNumber = parseInt(perPage, 10);

      if (!Number.isNaN(perPageNumber)) {
        this.perPage = perPageNumber;
      }

      if (!!status) {
        this.status = status;
      }
    },
    /**
     * Sets the local filters as url query parameters.
     */
    async setFiltersAsQueryParameters() {
      const filters: any = {};

      this.filters.forEach((filter: Record<string, any>) => {
        if (!!this[filter.variable]) {
          filters[filter.key] = this[filter.variable];
        }
      });

      const queryObject: any = {
        filters,
        perPage: this.perPage,
        status: this.status,
        page: this.page,
      };

      if (!!this.order && !!this.order.key) {
        queryObject.order = this.order;
      }

      await this.setFilters();

      let query: any = qs.stringify(queryObject, { encode: false });
      query = querystring.parse(query);

      this.$router.push({
        name: 'user.index',
        query,
      });
    },
    async doSearch() {
      await this.setFiltersAsQueryParameters();
      await this.loadUsers();
    },
    async loadUsers(page?: number) {
      try {
        this.loaded = false;

        const filters: any = {};

        this.filters.forEach((filter: Record<string, any>) => {
          if (!!this[filter.variable]) {
            filters[filter.key] = this[filter.variable];
          }
        });

        const query: any = {
          filters,
          order: this.order,
          perPage: this.perPage,
          status: this.status,
          page: !!page ? page : this.page,
        };

        const response = await api.admin.user.allPaginated(query);

        this.setResponse(response);

        this.users = response.data;
      } catch (e: any) {
        console.error(e);

        this.$toast.error(e.response.data.message);
      } finally {
        this.loaded = true;
      }
    },
    async showUserDetails(user: User) {
      this.currentUser = user;

      if (this.offCanvasToShow !== 'the-admin-user-details-offcanvas') {
        this.openOffCanvas('the-admin-user-details-offcanvas');
      }
    },
    async impersonate(user: User) {
      await this.$modal.confirm({
        title: this.$t('admin.user.loginAs'),
        text: this.$t('admin.user.impersonateText', { user: user.name }),
        confirmButtonText: this.$t('general.button.confirm'),
        confirmButtonCallback: async () => {
          try {
            await this.authStore.impersonateStart(user.id);
          } catch (e) {
            await this.$modal.info({
              title: this.$t('admin.user.impersonateErrorTitle'),
              text: this.$t('admin.user.impersonateErrorText'),
            });
          }
        },
      });
    },
    async toggleAccess(user: User) {
      const title = user.canAccess ? this.$t('admin.user.denyUserTitle') : this.$t('admin.user.forgiveUserTitle');
      const text = user.canAccess ? this.$t('admin.user.denyUserText', { user: user.name }) : this.$t('admin.user.forgiveUserText', { user: user.name });
      const confirmButtonText = user.canAccess ? this.$t('admin.user.denyAccess') : this.$t('admin.user.provideAccess');

      await this.$modal.confirm({
        title,
        text,
        confirmButtonText,
        confirmButtonCallback: async () => {
          try {
            await api.admin.user.forgiveAccess(user.id);
            this.$toast.success(this.$t('general.shared.savedChanges'));
            await this.loadUsers();
          } catch (e: any) {
            console.error(e.response.data);
            this.$toast.error(e.response.data.message);
          }
        },
      });
    },
    cleanCurrentUser() {
      this.currentUser = null;
    },
    async handlePageChangedEvent(page: number) {
      await this.loadUsers(page);
    },
    toggleOrder(field: string) {
      const { order } = this.order;

      if (!order) {
        this.order.order = 'ASC';
      } else {
        this.order.order = order === 'ASC' ? 'DESC' : 'ASC';
      }

      this.order.key = field;

      this.search();
    },
    setFilters() {
      this.filterOptions = this.filters.flatMap((filter: Record<string, any>) => {
        const {
          key,
          multiple,
          isConcept,
          labelAux,
          conceptName,
          variable,
        } = filter;

        const options = multiple ? this[variable] : [this[variable]];

        return options.map((option: any) => {
          const value = isConcept ? findConceptByKey(`${ conceptName }`, option) : option;
          const label = this.$t(`${ labelAux }`, { value: !!conceptName ? value.name : value });
          return { key, value: !!conceptName ? value.key : value, label };
        });
      });
    },
    removeFilter(filter: Record<string, string>) {
      this.filters.forEach((filterAux: Record<string, string | boolean>) => {
        if (filterAux.key === filter.key) {
          if (!filterAux.multiple) {
            this[filterAux.variable] = '';
          } else {
            this[filterAux.variable] = this[filterAux.variable].filter((option: string) => option !== filter.value);
          }
        }
      });

      if (filter.key === 'status') {
        this.status = 'active';
      }

      this.doSearch();
    },
  },
});
</script>

<style scoped lang="scss">
.admin-users-wide {
  max-width: 1550px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

@media (min-width: 1400px) {
  .admin-users-wide {
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }
}

.image-container {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  border-radius: 6px !important;
  background-color: #fd0c6f !important;
}

.admin-users-actions-cell {
  white-space: nowrap;
}

:deep(.btn:not(.rounded-circle)) {
  border-radius: 6px !important;
}

:deep(.btn-primary) {
  background-color: #fd0c6f !important;
  border-color: #fd0c6f !important;
  color: #fff !important;
}

:deep(.btn-primary:hover) {
  background-color: #dc095f !important;
  border-color: #dc095f !important;
}

:deep(.btn-order) {
  font-size: 12px !important;
  font-weight: 700 !important;
}

:deep(.input-group-text),
:deep(.form-control),
:deep(.form-select) {
  border-radius: 6px !important;
}

:deep(.input-group-text) {
  min-height: 36px;
}

:deep(.form-control),
:deep(.form-select) {
  min-height: 36px;
  font-size: 13px;
}
</style>
